import type { VisitType } from '../constants/visitType'
import type { ReviewProps } from '../types/review'
import { pageCache } from './pageCache'

/**
 * 사진 뷰어가 그릴 내용. 들어가기 전에 미리 채워둘 수 있다.
 *
 * 왜 미리 채우나
 * --------------
 * 썸네일을 누르면 뷰어는 기록 상세와 댓글 수를 차례로 받아온 뒤에야 화면을
 * 그렸다. 그 동안 전체가 크림색 스피너였다. 실기기 녹화를 60fps로 뜯어
 * 재보니 0.3초(18프레임) 동안 화면 전체가 #FAF6EF 한 색이었다 — 사람 눈에는
 * 흰 화면이 한 번 번쩍이는 것으로 보인다.
 *
 * 그런데 그 내용은 피드가 이미 전부 갖고 있다. 사진 주소도 같은 주소라
 * 브라우저에 이미 받아져 있다. 누르는 순간 넘겨주면 기다릴 것이 없다.
 *
 * 받아오기는 그대로 돌아서, 늦게 도착한 정확한 값으로 조용히 덮인다.
 */
export interface PhotoViewerData {
  placeName: string
  placeAddress: string
  placeId: string
  rating: number
  visitDate: string | null
  createdAt: string | null
  visitType: VisitType
  /** 이 장소의 몇 번째 기록인지 — 삭제 확인 문구에 쓴다 */
  visitNumber?: number
  reviewText: string | null
  authorName: string | null
  images: string[]
  imageBlurs: (string | null)[]
  commentCount: number
  likeCount: number
  isLiked: boolean
  hasUserReview: boolean
  isBookmarked: boolean
  userId: string | null
}

export function photoViewerCacheKey(reviewId: string | number): string {
  return `review-photo:${reviewId}`
}

/**
 * 피드가 쓰는 날짜는 `2026.10.03` 꼴이고 뷰어는 Date로 파싱한다.
 * 점은 엔진마다 해석이 갈려 하이픈으로 맞춘다.
 */
function toParsableDate(date: string | undefined): string | null {
  if (!date) return null

  return /^\d{4}\.\d{2}\.\d{2}$/.test(date) ? date.replace(/\./g, '-') : date
}

/**
 * 썸네일을 누르는 순간, 피드가 아는 내용을 뷰어 자리에 미리 놓는다.
 *
 * 사진이 없는 기록은 뷰어가 '사진 없음'으로 빠지므로 건드리지 않는다.
 */
export function primePhotoViewer(review: ReviewProps): void {
  const images = review.images ?? []
  if (images.length === 0) return

  const data: PhotoViewerData = {
    placeName: review.restaurant.name,
    placeAddress: review.placeAddress ?? '',
    placeId: String(review.placeId),
    rating: review.restaurant.rating,
    visitDate: toParsableDate(review.date),
    createdAt: null,
    visitType: review.visitType,
    visitNumber: review.visitNumber,
    reviewText: review.reviewText ?? null,
    // 뷰어는 아이디를 먼저 쓴다. 순서를 맞춰야 늦게 온 값으로 덮일 때 안 바뀐다.
    authorName: review.user.username ?? review.user.nickname ?? null,
    images,
    imageBlurs: review.imageBlurs ?? [],
    commentCount: review.comments,
    likeCount: review.likes,
    isLiked: review.isLiked ?? false,
    hasUserReview: review.hasUserReview ?? false,
    isBookmarked: review.isBookmarked ?? false,
    userId: review.userId ?? null,
  }

  pageCache.set(photoViewerCacheKey(review.id), data)
}
